import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * GraphQL against tinywins-community AppSync (shared TinyWins Cognito).
 * Message and Notification CRUD use Cognito JWT — no public API key.
 */

export function getCommunityGraphqlUrl(): string {
  const fromEnv = (import.meta.env.VITE_COMMUNITY_GRAPHQL_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  throw new Error(
    'Community messaging API is not configured. Set VITE_COMMUNITY_GRAPHQL_URL to the tinywins-community AppSync GraphQL URL (from that app’s amplify_outputs.json → data.url).',
  );
}

export function isCommunityMessagingConfigured(): boolean {
  return Boolean((import.meta.env.VITE_COMMUNITY_GRAPHQL_URL as string | undefined)?.trim());
}

async function getUserPoolJwt(): Promise<string> {
  const session = await fetchAuthSession();
  const token =
    session.tokens?.accessToken?.toString() || session.tokens?.idToken?.toString() || '';
  if (!token) {
    throw new Error('You must be signed in to use messages and notifications.');
  }
  return token;
}

export async function communityGraphqlRequest<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  const url = getCommunityGraphqlUrl();
  const token = await getUserPoolJwt();

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ query, variables }),
  });

  const text = await res.text();
  let json: { data?: T; errors?: Array<{ message?: string }> };
  try {
    json = JSON.parse(text) as { data?: T; errors?: Array<{ message?: string }> };
  } catch {
    throw new Error(
      res.ok
        ? 'Invalid response from community API (not JSON).'
        : `Community API request failed (${res.status}). ${text.slice(0, 200)}`,
    );
  }

  if (!res.ok) {
    const msg = json.errors?.map((e) => e.message).join('; ') || text.slice(0, 300);
    throw new Error(`Community API request failed (${res.status}): ${msg}`);
  }
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors.map((e) => e.message || 'GraphQL error').join('; '));
  }
  return json.data as T;
}
